import React, { useEffect, useRef, useState } from 'react';
import { Box, Flex, Icon, Progress, Text } from '@chakra-ui/react';
import { FaClock } from 'react-icons/fa';
import {
	SESSION_IDLE_LOCK_MS,
	SESSION_IDLE_LOGOUT_MS,
	formatSessionCountdown,
} from '../../constants/sessionIdle';
import { getLastActivityAt } from '../../utils/sessionIdleStorage';

const getRemainingMs = () =>
	Math.max(0, SESSION_IDLE_LOGOUT_MS - (Date.now() - getLastActivityAt()));

const LOCK_WINDOW_MS = Math.max(
	1,
	SESSION_IDLE_LOGOUT_MS - SESSION_IDLE_LOCK_MS,
);
const GREEN = '#16a34a';
const ORANGE = '#d97706';
const RED = '#dc2626';

const lerp = (from, to, t) => from + (to - from) * t;

const hexToRgb = (hex) => {
	const n = parseInt(hex.slice(1), 16);
	return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};

const rgbToHex = ({ r, g, b }) =>
	`#${[r, g, b]
		.map((value) => Math.round(value).toString(16).padStart(2, '0'))
		.join('')}`;

const lerpColor = (from, to, t) => {
	const a = hexToRgb(from);
	const b = hexToRgb(to);
	return rgbToHex({
		r: lerp(a.r, b.r, t),
		g: lerp(a.g, b.g, t),
		b: lerp(a.b, b.b, t),
	});
};

const colorForProgress = (progress) => {
	const t = 1 - Math.min(1, Math.max(0, progress / 100));
	if (t <= 0.5) return lerpColor(GREEN, ORANGE, t / 0.5);
	return lerpColor(ORANGE, RED, (t - 0.5) / 0.5);
};

const SessionLockCountdown = ({ onExpired }) => {
	const [remainingMs, setRemainingMs] = useState(getRemainingMs);
	const expiredRef = useRef(false);
	const onExpiredRef = useRef(onExpired);

	useEffect(() => {
		onExpiredRef.current = onExpired;
	}, [onExpired]);

	useEffect(() => {
		const tick = () => {
			const next = getRemainingMs();
			setRemainingMs(next);
			if (next <= 0 && !expiredRef.current) {
				expiredRef.current = true;
				onExpiredRef.current?.();
			}
		};

		tick();
		const intervalId = window.setInterval(tick, 250);
		return () => window.clearInterval(intervalId);
	}, []);

	const progressValue = Math.min(100, (remainingMs / LOCK_WINDOW_MS) * 100);
	const accent = colorForProgress(progressValue);

	return (
		<Box
			px={3}
			pt={1.5}
			pb={2}
			borderRadius='lg'
			border='1px solid'
			borderColor='green.100'
			bg='green.50'>
			<Flex align='center' justify='center' gap={2} mb={1.5}>
				<Icon as={FaClock} boxSize={3} color={accent} />
				<Text fontSize='xs' mb={0} color='gray.600'>
					Sign out in
				</Text>
				<Text
					fontSize='sm'
					fontWeight='700'
					fontVariantNumeric='tabular-nums'
					mb={0}
					style={{ color: accent }}>
					{formatSessionCountdown(remainingMs)}
				</Text>
			</Flex>
			<Progress
				value={progressValue}
				size='xs'
				borderRadius='full'
				bg='blackAlpha.100'
				sx={{
					'& > div': {
						background: accent,
						transition: 'background 0.2s linear, width 0.25s linear',
					},
				}}
			/>
		</Box>
	);
};

export default SessionLockCountdown;
